import { useState } from 'react'
import { MultiTableAnalysisWidgetData } from '../../../types/gotypes'

/**
 * Message shown when the server rejects a text save because someone else changed
 * the dashboard first (409 stale_dashboard). The text stays in the editor — the
 * viewer's words are never the thing we throw away.
 */
export const CARD_SAVE_CONFLICT_MESSAGE =
  'Not saved — someone else changed this dashboard while you were editing. Your text is still here. Reload to see their version, then save again.'

interface CardTextEditorOptions {
  /**
   * Persists the card. Rejects when the write was refused; the editor then stays
   * open with the text intact instead of pretending the save happened (#963).
   */
  onUpdateData?: (data: MultiTableAnalysisWidgetData) => void | Promise<void>
  /** Records the change in the dashboard changelog. Only called for a write that landed. */
  onRecordEdit?: (beforeText: string, afterText: string) => void
}

/**
 * Owns the text-edit surface of a Gen AI insight card: the draft, the in-flight
 * save, and the local bridge that shows a saved edit before the query cache
 * catches up.
 *
 * The bridge (`localEditedReport`) is only set once the write has landed. Setting
 * it optimistically made a rejected edit keep rendering while the server held
 * different text.
 */
export function useCardTextEditor({ onUpdateData, onRecordEdit }: CardTextEditorOptions) {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState('')
  const [localEditedReport, setLocalEditedReport] = useState<string | undefined>(undefined)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const handleStartEdit = (currentText: string) => {
    setSaveError(null)
    setEditText(currentText)
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditText('')
    setSaveError(null)
  }

  /**
   * Saves the draft. Returns true when the write landed. On failure the editor
   * stays open, the draft is untouched, and no changelog entry is written for an
   * edit the server refused.
   */
  const handleSaveEdit = async (data: MultiTableAnalysisWidgetData, beforeText: string): Promise<boolean> => {
    const afterText = editText
    setSaveError(null)
    setIsSaving(true)
    try {
      await onUpdateData?.({ ...data, edited_report: afterText })
    } catch {
      setIsSaving(false)
      setSaveError(CARD_SAVE_CONFLICT_MESSAGE)
      return false
    }
    setIsSaving(false)
    setLocalEditedReport(afterText)
    setIsEditing(false)
    if (beforeText !== afterText) {
      onRecordEdit?.(beforeText, afterText)
    }
    return true
  }

  return {
    isEditing,
    editText,
    setEditText,
    localEditedReport,
    setLocalEditedReport,
    isSaving,
    saveError,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
  }
}

export default useCardTextEditor
